import { UseQueryOptions } from '@tanstack/react-query';

import { ContentType, ContentTypeEnumForBackend } from 'domains/content/types';
import useQuery from 'hooks/useQuery';
import { CuredApiPaths, SortDirection } from 'models/enums';

import QueryKeys from './keys';

export type PaginatedContentListResponse = {
    total: number;
    offset: number;
    limit: number;
    data: unknown[];
};

type UsePaginatedContentParams = {
    options?: {
        folderId?: number;
        type?: string;
        searchTerm?: string;
        statuses?: string[];
        limit?: number;
        offset?: number;
        sortColumn?: string;
        sortDirection?: SortDirection;
    };
    reactQueryOptions?: UseQueryOptions<PaginatedContentListResponse, Error>;
};

const usePaginatedContent = ({ options = {}, reactQueryOptions }: UsePaginatedContentParams = {}) => {
    const { folderId, type, searchTerm, statuses, limit, offset, sortColumn, sortDirection } = options;

    let apiType = type;
    if (type === ContentType.PAGE) {
        apiType = ContentTypeEnumForBackend['Landing Page'];
    }

    const queryKey = QueryKeys.CONTENT_PAGINATED(
        folderId,
        type,
        searchTerm,
        statuses?.join(','),
        offset,
        limit,
        sortColumn,
        sortDirection
    );

    const params: Record<string, string | number> = {};
    if (folderId !== undefined && folderId > 0) {
        params.folderId = String(folderId);
    }
    if (type && type !== ContentType.ALL) {
        params.type = apiType as string;
    }
    if (searchTerm?.trim()) {
        params.searchTerm = searchTerm.trim();
    }
    if (statuses && statuses.length > 0) {
        params.status = statuses.join(',');
    }
    if (limit !== undefined) {
        params.limit = limit;
    }
    if (offset !== undefined) {
        params.offset = offset;
    }
    if (sortColumn && sortDirection) {
        params.sort = `${sortColumn}:${sortDirection}`;
    }

    const axiosOptions = Object.keys(params).length > 0 ? { params } : {};

    return useQuery<PaginatedContentListResponse>(
        queryKey,
        CuredApiPaths.CONTENT_PAGINATED,
        reactQueryOptions,
        axiosOptions
    );
};

export default usePaginatedContent;
